<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :ref="question.questionId || question.questionComponent"
      :key="question.questionId || question.questionComponent"
      product-name="bike"
      step-id="drivers"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      step-type="multiple"
      :step-index="driverIndex"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import { inject } from '@vue/composition-api';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-bike-step-2',
  inject: ['helpers'],
  props: {
    showConfirmModal: {
      type: Function,
      required: true,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    driverIndex: {
      type: Number,
      required: true,
    },
  },
  data () {
    return {
      stepData: null,
    };
  },
  computed: {
    medicalConditionsHelpText () {
      const getHelpTextValue = (isAdditional) => `
        <p>
            If ${isAdditional ? 'they' : 'you'} are unsure if ${isAdditional ? 'they' : 'you'} have any medical conditions that will affect ${isAdditional ? 'their' : 'your'} driving, please ${isAdditional ? 'ask them to ' : ''} check the DVLA website or ask a doctor and inform the DVLA if necessary.
        </p>
        <p>
          If you have not declared a condition or informed the DVLA and ${isAdditional ? 'either of ' : ''}you make a claim, then your claim may be refused and your insurance will be void.
        </p>
      `;

      return {
        value: getHelpTextValue(false),
        valueAdditional: getHelpTextValue(true),
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Declaring medical conditions',
      };
    },
    periodOfLicenceHeldHelpText () {
      const getHelpTextTitle = (isAdditional) => `The amount of years selected needs to be the number of full years since ${isAdditional ? 'they' : 'you'} received ${isAdditional ? 'their' : 'your'} licence e.g. If ${isAdditional ? 'they' : 'you'} passed ${isAdditional ? 'their' : 'your'} test 4 years and 11 months ago, ${isAdditional ? 'they' : 'you'} must answer 4 years.`;
      const getHelpTextValue = (isAdditional) => `
        <p>${isAdditional ? 'They' : 'You'} will need to enter the month ${isAdditional ? 'they' : 'you'} passed ${isAdditional ? 'their' : 'your'} test if it was less than 4 years ago. If ${isAdditional ? 'they' : 'you'} haven’t passed ${isAdditional ? 'their' : 'your'} test yet, this needs to be the number of years since ${isAdditional ? 'they' : 'you'} received ${isAdditional ? 'their' : 'your'} provisional licence.</p>
        <p>On a UK  licence, ${isAdditional ? 'they' : 'you'} can find the ‘valid from’ date on the back of ${isAdditional ? 'their' : 'your'} licence.</p>
      `;

      return {
        title: getHelpTextTitle(false),
        titleAdditional: getHelpTextTitle(true),
        value: getHelpTextValue(false),
        valueAdditional: getHelpTextValue(true),
        collapsible: true,
        openerText: this.isExperimentMinimiseHelptext ? 'Provide an accurate licence date' : 'Show More',
        closerText: this.isExperimentMinimiseHelptext ? 'Provide an accurate licence date' : 'Hide',
      };
    },
    questions () {
      return [
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'title',
          params: {
            customFields: {
              field: 'title',
            },
            title: 'What is your title?',
            titleAdditional: 'What is their title?',
            helpText: {
              value: 'If your title is not in the list, please select the most appropriate for you.',
              valueAdditional: 'If their title is not in the list, please select the most appropriate for them',
            },
            optionsConfig: {
              options: this.helpers.getTitleOptions(this.isMxDrLiveBike),
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a title.',
              },
            },
          },
        },
        {
          questionComponent: 'question-name',
          questionId: 'name',
        },
        {
          questionComponent: 'question-date-of-birth',
          questionId: 'date-of-birth',
        },
        {
          questionComponent: 'question-uk-resident',
          questionId: 'uk-resident',
        },
        {
          questionComponent: 'question-marital-status',
          questionId: 'marital-status',
          params: {
            helpText: `
              <p>
                There might be several options that fit you - so choose the way that you would like to be described.
              </p>
              <p>
                Common law includes co-habiting/living with a partner.
              </p>
            `,
          },
        },
        {
          questionComponent: 'question-relationship-proposer',
          questionId: 'relationship-proposer',
          params: {
            onlyForAdditionals: true,
            validationErrors: {
              relationshipProposer: {
                is_not: 'Please confirm their relationship to you.',
              },
            },
          },
        },
        {
          questionComponent: 'question-telephone',
          questionId: 'telephone',
          params: {
            hiddenForAdditionals: true,
            helpText: {
              value: this.isExperimentMinimiseHelptext && 'Your insurance provider may need to contact you to discuss your policy. Later in this form, we’ll also ask you if you want to share your number with the providers who offer you the lowest prices, to discuss your insurance needs before you buy (this is a free service and completely optional).',
              openerText: 'Why do I need to enter a phone number?',
              collapsible: this.isExperimentMinimiseHelptext,
            },
          },
        },
        {
          questionComponent: 'question-address',
          questionId: 'address',
          params: {
            hiddenForAdditionals: true,
          },
        },
        {
          questionComponent: 'question-kept-address',
          questionId: 'kept-address',
          params: {
            hiddenForAdditionals: true,
            title: 'Is the bike kept at this address overnight?',
            subtitle:
              'Please enter the postcode of the location where the bike is kept overnight',
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'home-owner',
          params: {
            customFields: {
              field: 'homeOwner',
            },
            hiddenForAdditionals: true,
            title: 'Do you own your own home?',
            helpText: {
              value: 'Some of our insurance providers may offer you a discount if you are a home owner.',
              priceSensitive: true,
            },
          },
        },
        {
          questionComponent: 'question-email',
          questionId: 'email',
          params: {
            hiddenForAdditionals: true,
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'use-of-other-vehicles',
          params: {
            customFields: {
              field: 'useOfOtherVehicles',
            },
            optionsConfig: {
              options: {
                X: 'No Access to Any Other Vehicles',
                N: 'Named Driver on Another Person\'s Policy',
                O: 'Own/Use of a Car',
                M: 'Own/Use of Another Motorcycle',
                V: 'Own/Use of a Van',
                S: 'Company Car (Including Social Domestic and Pleasure Use)',
                C: 'Company Car (Within Working Hours)',
              },
            },
            title: 'Do you drive any other vehicle than the one to be insured?',
            titleAdditional:
              'Does this rider drive any other vehicles, other than the one to be insured?',
            helpText: {
              value: 'Some of our insurance providers may give a discount if you drive more than one vehicle.',
              valueAdditional: 'Some of our insurance providers may give a discount if they drive more than one vehicle.',
              priceSensitive: true,
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select an option from this list.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'employment-status',
          params: {
            customFields: {
              field: 'employmentStatus',
            },
            title: 'What is your employment status?',
            titleAdditional: 'What is their employment status?',
            optionsConfig: {
              otherPlaceholder: 'Please select employment status',
              mainItems: ['E', 'S', 'F', 'R'],
              options: {
                E: 'Employed',
                F: 'Full-Time Education',
                S: 'Self Employed',
                R: 'Retired',
                U: 'Unemployed',
                D: 'Director',
                P: 'Proprietor or Partner',
                H: 'Houseperson',
                I: 'Independent Means',
                N: 'Not Employed Due to Disability',
                G: 'Government',
                C: 'Club or Association',
              },
            },
          },
        },
        {
          questionComponent: 'question-occupation',
          questionId: 'occupation',
        },
        {
          questionComponent: 'question-business',
          questionId: 'business',
        },
        {
          questionComponent: 'question-another-job',
          questionId: 'another-job',
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-driving',
          params: {
            customFields: {
              field: 'typeOfDriving',
            },
            title: 'What kind of motorcycle driving licence do you have?',
            titleAdditional: 'What kind of motorcycle driving licence do they have?',
            helpText: {
              value: '<strong>Why do we ask?</strong><br>The type of licence you have shows the insurance providers how experienced you are.',
              valueAdditional: '<strong>Why do we ask?</strong><br>The type of licence they have shows the insurance providers how experienced they are.',
            },
            optionsConfig: {
              otherPlaceholder: 'Please select type of licence',
              mainItems: ['F', 'P', 'M'],
              options: {
                F: 'Full UK Motorcycle',
                M: 'Full UK Moped',
                P: 'Provisional UK Motorcycle',
                EEC: 'Full EU Motorcycle',
                PE: 'Provisional EU Motorcycle',
                I: 'Full International Motorcycle',
                PI: 'Provisional International Motorcycle',
                E: 'Other Euro Full Bike',
                PEO: 'Other Euro Provisional Bike',
              },
            },
            validationErrors: {
              field: {
                required: 'Please select your motorcycle licence type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-period-of-licence-held',
          questionId: 'period-of-licence-held',
          params: {
            helpText: this.periodOfLicenceHeldHelpText,
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'has-car-licence',
          params: {
            customFields: {
              field: 'hasCarLicence',
            },
            hiddenForAdditionals: true,
            title: 'Do you hold a car licence?',
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'car-type-of-driving',
          params: {
            customFields: {
              field: 'carTypeOfDriving',
            },
            title: 'What kind of car driving licence do you have?',
            hiddenForAdditionals: true,
            helpText: {
              value: '<strong>Why do we ask?</strong><br>The type of license you have shows the insurance providers how experienced you are behind the wheel.',
            },
            resetAfterChanges: [
              'hasCarLicence',
            ],
            optionsConfig: {
              otherPlaceholder: 'Please select type of car licence',
              mainItems: ['F', 'P', 'A', 'E'],
              options: {
                F: 'Full UK',
                P: 'Provisional UK',
                A: 'Automatic UK',
                E: 'EU Full',
                EP: 'EU Provisional',
                O: 'Other European',
                OI: 'Other International',
              },
            },
            renderAfter: {
              hasCarLicence: {
                is: {
                  other: true,
                },
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a driver licence type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-period-of-licence-held',
          questionId: 'car-period-of-licence-held',
          params: {
            customFields: {
              periodOfLicenceHeld: 'carPeriodOfLicenceHeld',
            },
            hiddenForAdditionals: true,
            title: 'How many years have you held this car licence?',
            helpText: this.periodOfLicenceHeldHelpText,
            showRules: {
              hasCarLicence: {
                is: {
                  other: true,
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-licence-number',
          questionId: 'licence-number',
          params: {
            title: 'Would you like to provide your Driving Licence Number, which may reduce your premium with some insurance providers on our panel?',
            titleAdditional:
              'Would you like to provide their Driving Licence Number, which may reduce your premium with some insurance providers on our panel?',
            helpText: {
              value: 'Some of our insurance providers may give a discount if you provide your driving licence number here.',
              valueAdditional: 'Some of our insurance providers may give a discount if you provide their driving licence number here.',
            },
          },
        },
        {
          questionComponent: 'question-driver-cbt',
          questionId: 'driver-cbt',
          params: {
            dateColumnsAlignment: {
              month: {
                cols: '12',
                sm: '6',
              },
              year: {
                cols: '12',
                sm: '6',
              },
            },
            isDayShown: false,
            renderAfter: ['dateOfBirth'],
            hiddenValues: {
              driverCbt: false,
            },
            showRules: {
              typeOfDriving: {
                is: {
                  other: 'P',
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'driver-experience',
          params: {
            customFields: {
              field: 'driverExperience',
            },
            title: 'How many years continuous riding experience do you have?',
            titleAdditional: 'How many years continuous riding experience does this rider have?',
            helpText: 'Continuous here means not having a break from an insurance policy for more than 2 months.',
            placeholder: 'e.g. 5',
            maxLength: 2,
            validationRules: {
              field: {
                required: true,
                integer: true,
                minValue: 0,
                maxValue: 99,
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter a year.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'riding-qualifications',
          params: {
            customFields: {
              field: 'ridingQualifications',
            },
            title: 'Do you have any riding qualifications?',
            titleAdditional: 'Does this rider have any riding qualifications?',
            helpText: {
              value: 'Some insurance providers may offer a discount if you have additional qualifications.',
              valueAdditional: 'Some insurance providers may offer a discount if they have additional qualifications.',
              priceSensitive: true,
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a riding qualification from the list.',
              },
            },
            optionsConfig: {
              placeholder: 'Please select riding qualifications',
              options: {
                N: 'No',
                IAM: 'Institute of Advanced Motorists (IAM) Motorcycle Test',
                APM: 'Advanced Police Motorcyclists',
                BS: 'Bike Safe',
                BBR: 'BMF Blue Riband',
                BCP: 'BMF Certificate of Proficiency',
                DCC: 'DSA Certificate Of Competence (Enhanced Rider Bonus)',
                DSA: 'Driving Standards Agency - Examiners Motorcycle test',
                RSP: 'Royal Society for Provention of Accidents (RoSPA)',
                E: 'The Edge ',
                VAR: 'Vision Advanced Rider Training Qualification',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'biking-organisation',
          params: {
            customFields: {
              field: 'bikingOrganisation',
            },
            title: 'Are you a member of a biking organisation?',
            titleAdditional: 'Is the rider a member of a biking organisation?',
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a biking qualification from the list.',
              },
            },
            optionsConfig: {
              placeholder: 'Please select a biking organisation',
              options: {
                N: 'None',
                BMF: 'British Motorcycle Federation (BMF)',
                BMO: 'British Motorcycle Owners Club',
                IAM: 'Institue of Advanced Motorists',
                MAG: 'Motorcycle Action Group (MAG)',
                NABD: 'National Association for Bikers with a Disability',
                NSRA: 'National Scooter Riders Association',
                YBMO: 'Your Bike Manufacturers Owners Club',
                VJM: 'Vintage Japanese Motorcycle Club',
                VM: 'Vintage Motorcycle Club',
                WIM: 'Womens International Motorcycle Association',
              },
            },
          },
        },
        {
          questionComponent: 'question-medical-conditions',
          questionId: 'medical-conditions',
          params: {
            helpText: this.medicalConditionsHelpText,
          },
        },
        {
          questionComponent: 'question-base-claims',
          questionId: 'claims',
          params: {
            customFields: [
              'hadAnyAccidents',
              'claims',
              'claims-new',
            ],
            defaultValues: {
              claims: [],
            },
            title: 'Have you had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            titleAdditional: 'Have they had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            helpText: {
              value: "Insurers need to know about all claims and incidents in the last 5 years, even if it wasn't your fault or if you were driving another vehicle.",
              valueAdditional: "Insurers need to know about all claims and incidents in the last 5 years, even if it wasn't their fault or if they were driving another vehicle.",
            },
            questions: [
              {
                questionId: 'date-of-claim',
                questionComponent: 'question-base-date',
                params: {
                  customFields: {
                    field: 'dateOfClaim',
                  },
                  title: 'When did the incident happen?',
                  prevalidationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter the date.',
                      dateOfIncident: 'The incident must be within the last five years.',
                      checkFutureIncident: 'The incident date must not be in the future.',
                    },
                  },
                },
              },
              {
                questionId: 'cause-of-description',
                questionComponent: 'question-base-dropdown',
                params: {
                  customFields: {
                    field: 'causeOfDescription',
                  },
                  title: 'What kind of incident or claim was it?',
                  optionsConfig: {
                    options: {
                      1: 'Incident - no claim made',
                      2: 'Accident - not settled',
                      3: 'Accident - not settled and persons injured',
                      4: 'Accident - driver at fault, but no injuries',
                      5: 'Accident - driver at fault with persons injured',
                      6: 'Accident - driver not at fault and recovered all costs and excess',
                      7: 'Accident - driver not at fault and full recovery, but persons injured',
                      8: 'Accident - single vehicle',
                      9: 'Storm or flood damage',
                      10: 'Fire damage',
                      11: 'Vandalism',
                      12: 'Theft - vehicle from outside home, but was recovered',
                      13: 'Theft - vehicle from outside home, was not recovered',
                      14: 'Theft - vehicle parked away from home, but was recovered',
                      15: 'Theft - vehicle parked away from home, was not recovered',
                      16: 'Theft - vehicle contents stolen from outside home',
                      17: 'Theft - vehicle contents stolen, was parked away from home',
                      18: 'Windscreen/glass',
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please select an option from this list.',
                    },
                  },
                },
              },
              {
                questionId: 'current-policy',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'currentPolicy',
                  },
                  title: 'Claim occurred on your most recent policy?',
                  titleAdditional: 'Claim occurred on their most recent policy?',
                },
              },
              {
                questionId: 'cost-of-repairs',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'costOfRepairs',
                  },
                  title: 'What was the total claim cost?',
                  helpText: {
                    value: 'If you are unsure your insurance company at the time of the incident should be able to give you this figure. If the claim is still to be settled they may be able to give a reserve figure.',
                    valueAdditional: 'If you are unsure their insurance company at the time of the incident should be able to give you this figure. If the claim is still to be settled they may be able to give a reserve figure.',
                  },
                  inputPrefix: '£',
                  placeholder: 'e.g. 250',
                  minLength: 1,
                  maxLength: 7,
                  hiddenValues: {
                    field: 0,
                  },
                  showRules: {
                    causeOfDescription: {
                      isNot: {
                        other: [1],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                      maxValue: 9999999,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      maxValue: 9999999,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter the total claim cost in £',
                    },
                  },
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-base-convictions',
          questionId: 'convictions-bike',
          params: {
            customFields: [
              'hadAnyConvictions',
              'convictions',
              'convictions-new',
            ],
            defaultValues: {
              convictions: [],
            },
            title: 'Have you had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            titleAdditional: 'Have they had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            helpText: {
              value: 'If unsure, conviction details can be found by viewing your driving licence information on the government website or you can also check via phone or post.',
              valueAdditional: 'If unsure, conviction details can be found by viewing their driving licence information on the government website or you can also check via phone or post.',
            },
            questions: [
              {
                questionId: 'date_of_conviction',
                questionComponent: 'question-base-date',
                params: {
                  customFields: {
                    field: 'dateOfConviction',
                  },
                  title: 'What date did the conviction happen?',
                  helpText: {
                    value: 'If unsure, conviction details can be found by viewing your driving licence information on the government website or you can also check via phone or post.',
                    valueAdditional: 'If unsure, conviction details can be found by viewing their driving licence information on the government website or you can also check via phone or post.',
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter the date.',
                      dateOfIncident: 'The conviction must be within the last five years.',
                      checkFutureIncident: 'The conviction date must not be in the future.',
                    },
                  },
                },
              },
              {
                questionId: 'conviction-declared-accident',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'convictionDeclaredAccident',
                  },
                  title: 'Does this conviction relate to a declared accident?',
                  helpText: {
                    value: 'Is this conviction related to any accidents you have had in the past 5 years?',
                    valueAdditional: 'Is this conviction related to any accidents they have had in the past 5 years?',
                  },
                },
              },
              {
                questionId: 'offence-code',
                questionComponent: 'question-base-search-other',
                params: {
                  customFields: {
                    field: 'offenceCode',
                    search: 'offenceCode',
                  },
                  title: 'What was the conviction for?',
                  searchType: 'getConvictionOffenceCodes',
                  topGroup: 'most_common_offence_codes',
                  isImmediate: true,
                  optionsConfig: {
                    placeholder: 'Please Select Conviction Code',
                    isCapitalize: true,
                  },
                  validationErrors: {
                    search: {
                      required: 'Please confirm the offence code.',
                    },
                  },
                },
              },
              {
                questionId: 'alcohol-method-test',
                questionComponent: 'question-base-radio-list',
                params: {
                  customFields: {
                    field: 'alcoholMethodTest',
                  },
                  title: 'What method was used to test for alcohol?',
                  optionsConfig: {
                    options: {
                      B: 'Breath',
                      L: 'Blood',
                      U: 'Urine',
                    },
                  },
                  showRules: {
                    offenceCode: {
                      isAny: {
                        other: [
                          'CD40',
                          'CD60',
                          'DR10',
                          'DR20',
                          'DR50',
                          'DR40',
                        ],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'alcohol-level',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'alcoholLevel',
                  },
                  title: 'What was the alcohol level recorded (mg/100 ml)?',
                  placeholder: 'e.g. 90',
                  minLength: 1,
                  maxLength: 3,
                  withoutSeparators: true,
                  showRules: {
                    offenceCode: {
                      isAny: {
                        other: [
                          'CD40',
                          'CD60',
                          'DR10',
                          'DR20',
                          'DR50',
                          'DR40',
                        ],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      minValue: 1,
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      minValue: 1,
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      minValue: 'Please enter the alcohol level e.g. 90.',
                      required: 'Please enter the alcohol level e.g. 90.',
                    },
                  },
                },
              },
              {
                questionId: 'points-received',
                questionComponent: 'question-base-dropdown',
                params: {
                  customFields: {
                    field: 'pointsReceived',
                  },
                  title: 'How many penalty points did you get for this offence? (please enter 0 if you did not receive any points)',
                  titleAdditional: 'How many penalty points did they get for this offence? (please enter 0 if they did not receive any points)',
                  optionsConfig: {
                    placeholder: 'Please select no. of points',
                    options: [
                      {
                        value: 0,
                        text: '0 Points',
                      },
                      {
                        value: 1,
                        text: '1 Point',
                      },
                      {
                        value: 2,
                        text: '2 Points',
                      },
                      {
                        value: 3,
                        text: '3 Points',
                      },
                      {
                        value: 4,
                        text: '4 Points',
                      },
                      {
                        value: 5,
                        text: '5 Points',
                      },
                      {
                        value: 6,
                        text: '6 Points',
                      },
                      {
                        value: 7,
                        text: '7 Points',
                      },
                      {
                        value: 8,
                        text: '8 Points',
                      },
                      {
                        value: 9,
                        text: '9 Points',
                      },
                      {
                        value: 10,
                        text: '10 Points',
                      },
                      {
                        value: 11,
                        text: '11 Points',
                      },
                      {
                        value: 12,
                        text: '12 Points',
                      },
                    ],
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please select an option from this list.',
                    },
                  },
                },
              },
              {
                questionId: 'how-much-you-fined',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'howMuchYouFined',
                  },
                  title: 'How much were you fined?',
                  titleAdditional: 'How much were they fined?',
                  placeholder: 'e.g. 100',
                  minLength: 0,
                  maxLength: 5,
                  inputPrefix: '£',
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter how much were you fined.',
                    },
                  },
                },
              },
              {
                questionId: 'received-ban-period',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'receivedBanPeriod',
                  },
                  title: 'If you received a ban, how many months was this for? (please enter 0 if you did not receive any ban)',
                  titleAdditional: 'If they received a ban, how many months was this for? (please enter 0 if they did not receive any ban)',
                  placeholder: 'Please enter no. of months',
                  minLength: 0,
                  maxLength: 2,
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter how many months were you banned for.',
                    },
                  },
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'had-any-non-mot-convictions',
          params: {
            customFields: {
              field: 'hadAnyNonMotConvictions',
            },
            title: 'Have you got any unspent non-motoring-related criminal convictions?',
            titleAdditional: 'Has this rider had any unspent non-motoring criminal convictions?',
            helpText: 'An unspent conviction is any conviction on a person\'s record which hasn\'t passed the time of the relevant rehabilitation period.',
          },
        },
      ];
    },
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  async created () {
    if (this.driverIndex > 0 && this.driverIndex < 5) {
      // additional driver
      this.$emit('tracking', 'driver-additional-' + this.driverIndex);
    } else {
      // standard driver tracking action
      this.$emit('tracking', 'driver');
    }
    await this.initStep();

    this.formObserver.reset();
  },
  methods: {
    ...mapActions(['showToast']),
    async initStep () {
      this.stepData = cloneDeep(this.form.drivers[this.driverIndex]);
    },
  },
  setup () {
    const {
      isMxDrLiveBike,
    } = inject('abTastyControllerUI');

    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isMxDrLiveBike,
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
